import { useCallback, useEffect, useRef } from 'react'

/**
 * Drag #sbResize to change the rail's width.
 *
 * The rail is anchored LEFT, so its width simply tracks the pointer's X — no delta
 * bookkeeping needed. Two details carried over from the original:
 *
 *  - `body.sb-resizing` kills the width/offset transitions while dragging, so the rail
 *    stays glued to the thumb instead of lagging behind it.
 *  - The width is written to `--sb-user-w`, which `--sb-panel-w` reads with a fallback
 *    (`var(--sb-user-w, 208px)`), so the docked offset `--sb-w` follows automatically
 *    and the panes shift in step.
 */
const MIN = 150
const MAX = 520
const KEY = 'wt_sb_w'

export function useSidebarResize() {
  const dragging = useRef(false)

  // Restore a saved width before first paint of the rail.
  useEffect(() => {
    try {
      const saved = Number(localStorage.getItem(KEY))
      if (Number.isFinite(saved) && saved >= MIN && saved <= MAX) {
        document.documentElement.style.setProperty('--sb-user-w', `${saved}px`)
      }
    } catch {
      // Width preference just won't persist.
    }
  }, [])

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault()
    dragging.current = true
    e.currentTarget.setPointerCapture(e.pointerId)
    document.body.classList.add('sb-resizing')
  }, [])

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging.current) return
    e.preventDefault()
    const w = Math.min(MAX, Math.max(MIN, Math.round(e.clientX)))
    document.documentElement.style.setProperty('--sb-user-w', `${w}px`)
  }, [])

  const onPointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging.current) return
    dragging.current = false
    try {
      e.currentTarget.releasePointerCapture(e.pointerId)
    } catch {
      // Capture may already be gone if the pointer left the window.
    }
    document.body.classList.remove('sb-resizing')
    const cur = document.documentElement.style.getPropertyValue('--sb-user-w')
    const px = Number.parseInt(cur, 10)
    if (Number.isFinite(px)) {
      try {
        localStorage.setItem(KEY, String(px))
      } catch {
        // Non-fatal.
      }
    }
  }, [])

  return { onPointerDown, onPointerMove, onPointerUp }
}
